'use client';

import type { RefObject } from 'react';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { PncBentoGrid } from '@/types/people-and-culture';
import { BENTO_CELL_CLASSES } from '../lib/people-and-culture.constants';

interface LifeAtWonderSectionProps {
  sectionRef: RefObject<HTMLDivElement>;
  zone: PncBentoGrid;
}

export default function LifeAtWonderSection({ sectionRef, zone }: LifeAtWonderSectionProps) {
  const items = zone.bentoItem ?? [];
  if (items.length === 0) return null;

  return (
    <section ref={sectionRef} className="bg-[#f8f8f7] px-4 sm:px-6 md:px-16 lg:px-24">
      <div data-reveal className="mb-5 flex flex-col gap-3 sm:mb-6 sm:gap-4">
        <h2 className="text-3xl font-bold text-[#0a0a0a] sm:text-4xl md:text-5xl">
          {zone.heading}
        </h2>
        {zone.subHeading ? (
          <p className="max-w-sm text-sm leading-relaxed text-gray-500 md:max-w-md">
            {stripHtml(zone.subHeading)}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 lg:h-[580px]">
        {items.map((cell, index) => {
          const imageUrl = getStrapiMediaUrl(cell.media, 'large');
          const className = BENTO_CELL_CLASSES[index] ?? BENTO_CELL_CLASSES[0];
          const featured = index === 1;

          return (
            <div
              key={cell.id}
              data-life-cell
              className={`relative overflow-hidden rounded-2xl transition-transform duration-500 hover:scale-[1.015] ${className}`}
            >
              <CmsImage
                src={imageUrl}
                alt={cell.title ?? ''}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                className="object-cover"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                {cell.pillText ? (
                  <span className="mb-2 inline-block rounded-full bg-[#ed1c22] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    {cell.pillText}
                  </span>
                ) : null}
                {featured ? (
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#ed1c22]">
                    {cell.title}
                  </p>
                ) : (
                  <p className="text-sm font-bold leading-snug text-white">{cell.title}</p>
                )}
                {(() => {
                  if (!cell.description) return null;
                  if (featured) {
                    return (
                      <p className="text-sm font-bold leading-snug text-white">
                        {stripHtml(cell.description)}
                      </p>
                    );
                  }
                  return (
                    <p className="mt-0.5 text-xs text-white/70">{stripHtml(cell.description)}</p>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
